/**
 * Classificazione applicazione odontogramma (dente / arcata / entrambe le arcate).
 *
 * Nota: selezionare entrambe le arcate (sup + inf) NON deve diventare "bocca":
 * Primolab non gestisce zone_number "bocca" (mostra "bocca null") e conta 1 riga.
 * Va inviato come 2 arcate (up + down).
 */

export type ApplicationKind = "dente" | "arcata" | "arcate" | "bocca";

/**
 * Unità con cui una voce di listino viene conteggiata e inviata a PrimoUp.
 *
 * `per_tooth`: il pulsante "arcata" dell'odontogramma è solo una selezione
 * rapida; 16 denti selezionati restano 16 elementi (es. 16 corone).
 * `per_arch`: una selezione completa vale una prestazione per arcata
 * (es. protesi totale o bite).
 * `per_mouth`: la prestazione vale una sola volta per l'intera bocca; verso
 * PrimoUp le zone restano comunque denti/arcate perché `bocca` non è supportata.
 */
export type PricingMode = "per_tooth" | "per_arch" | "per_mouth";

export type ServicePricingDescriptor = {
  pricing_mode?: PricingMode;
  nome_prestazione?: string;
  nome_visualizzato?: string;
};

export type TeethClassification = {
  kind: ApplicationKind;
  /** Elementi fatturabili (1 arcata = 1, entrambe = 2, denti = N) */
  billableCount: number;
  /** Zone Primolab: "up" | "down" oppure numeri dente */
  zones: string[];
  upperCount: number;
  lowerCount: number;
};

/**
 * Compatibilità per le voci create prima dell'introduzione di `pricing_mode`.
 * È volutamente conservativa: riconosce solo prestazioni inequivocabilmente
 * unitarie; tutte le altre mantengono il precedente comportamento per arcata.
 * Le voci nuove salvano sempre la scelta esplicita nel listino.
 */
export function inferLegacyPricingMode(
  service: ServicePricingDescriptor | null | undefined
): PricingMode {
  const label = `${service?.nome_visualizzato ?? ""} ${
    service?.nome_prestazione ?? ""
  }`
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase();

  const isClearlyPerTooth = [
    /\bCORON(?:A|E)\b/,
    /\bFACCETT(?:A|E)\b/,
    /\bINTARSI(?:O|I)\b/,
    /\bINLAY\b/,
    /\bONLAY\b/,
    /\bA ELEMENTO\b/,
    /\bAGGIUNTA DENTE\b/,
  ].some((pattern) => pattern.test(label));

  return isClearlyPerTooth ? "per_tooth" : "per_arch";
}

export function resolveServicePricingMode(
  service: ServicePricingDescriptor | null | undefined
): PricingMode {
  return service?.pricing_mode ?? inferLegacyPricingMode(service);
}

function isUpperTooth(tooth: string): boolean {
  const num = parseInt(tooth, 10);
  return num >= 11 && num <= 28;
}

function isLowerTooth(tooth: string): boolean {
  const num = parseInt(tooth, 10);
  return num >= 31 && num <= 48;
}

/** Classifica la selezione denti (senza impianti). */
export function classifyTeethSelection(selectedTeeth: string[]): TeethClassification {
  const unique = Array.from(new Set(selectedTeeth.map(String)));
  const upperTeeth = unique.filter(isUpperTooth);
  const lowerTeeth = unique.filter(isLowerTooth);
  const upperCount = upperTeeth.length;
  const lowerCount = lowerTeeth.length;
  const hasFullUpper = upperCount >= 16;
  const hasFullLower = lowerCount >= 16;

  // Entrambe le arcate complete → 2 elementi "arcata" (non "bocca")
  if (hasFullUpper && hasFullLower) {
    return {
      kind: "arcate",
      billableCount: 2,
      zones: ["up", "down"],
      upperCount,
      lowerCount,
    };
  }

  if (hasFullUpper && lowerCount === 0) {
    return {
      kind: "arcata",
      billableCount: 1,
      zones: ["up"],
      upperCount,
      lowerCount,
    };
  }

  if (hasFullLower && upperCount === 0) {
    return {
      kind: "arcata",
      billableCount: 1,
      zones: ["down"],
      upperCount,
      lowerCount,
    };
  }

  return {
    kind: "dente",
    billableCount: unique.length,
    zones: unique,
    upperCount,
    lowerCount,
  };
}

/** Una selezione per arcata deve contenere esattamente 16 o 32 denti completi. */
export function isCompleteArchSelection(selectedTeeth: string[]): boolean {
  const classification = classifyTeethSelection(selectedTeeth);
  return classification.kind === "arcata" || classification.kind === "arcate";
}

/** Classifica la selezione rispettando l'unità esplicita della prestazione. */
export function classifyTeethSelectionForService(
  selectedTeeth: string[],
  service: ServicePricingDescriptor | null | undefined
): TeethClassification {
  const classification = classifyTeethSelection(selectedTeeth);
  const pricingMode = resolveServicePricingMode(service);
  if (pricingMode === "per_arch") {
    return classification;
  }

  const unique = Array.from(new Set(selectedTeeth.map(String)));
  if (pricingMode === "per_mouth") {
    return {
      ...classification,
      billableCount: unique.length > 0 ? 1 : 0,
    };
  }

  return {
    ...classification,
    kind: "dente",
    billableCount: unique.length,
    zones: unique,
  };
}

/** Prezzo = prezzo unitario × quantità fatturabile stabilita dalla classificazione. */
export function priceForClassification(
  basePrice: number,
  classification: TeethClassification
): number {
  return basePrice * classification.billableCount;
}

/** Stringa compatibile con badge UI / calculatePrice (dente|arcata|arcate|bocca) */
export function applicationTypeLabel(classification: TeethClassification): string {
  return classification.kind;
}
